/**
 * Billing cycles — when each card closes, bills and falls due.
 *
 * The question this page exists to kill: *"which card's cycle is about to close,
 * and if I buy this now, when do I actually pay for it?"* Eight cards with eight
 * different answers is not something anyone should hold in their head.
 *
 * The page is ordered by what it is for. **Float ranking first**, because that is
 * the decision — every active card ranked by the interest-free days a purchase
 * made today would get. Then the per-card cycles, soonest to close. Then the
 * exact figures as a table, because a timeline is for shape and a table is for
 * numbers, and trying to make one do both produces neither.
 *
 * **Every projected date on this page is labelled as projected** (§2.7). The
 * projection is honest arithmetic on the card's own statement history, and the
 * reasoning is on hover for all of it — the rule, and how many statements agreed.
 * A card whose rule rests on one or two statements says `estimate`; a card with
 * no statements at all says what to do about it rather than showing a number.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CreditCard, Info } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge, Button, EmptyState, ErrorState, Panel, PageHeader } from "@/components/ui/primitives";
import { CycleBar } from "@/components/cycles/CycleBar";
import { cyclesApi, type CycleCard, type CycleConfidence } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";

export function CyclesPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["cycles"],
    queryFn: cyclesApi.list,
  });

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Panel>
          <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
        </Panel>
      </div>
    );
  }

  const cards = data?.cards ?? [];
  const known = cards.filter((c) => c.cycle_end !== null);
  const ranked = [...known]
    .filter((c) => c.float_days !== null)
    .sort((a, b) => (b.float_days ?? 0) - (a.float_days ?? 0));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={
          data
            ? `${formatDate(data.today)} · ${known.length} card${known.length === 1 ? "" : "s"} with a known cycle`
            : "Close and due dates"
        }
        title="Billing cycles"
        description="When each card's cycle closes and its bill falls due — learned from each card's own statement history."
      />

      {isLoading || !data ? (
        <LoadingState />
      ) : cards.length === 0 ? (
        <Panel>
          <EmptyState
            icon={CreditCard}
            title="No active cards"
            body="Add a card and MyDeck works out its cycle from the first statement it parses."
            action={
              <Button asChild variant="primary" size="sm">
                <Link to="/cards">Add a card</Link>
              </Button>
            }
          />
        </Panel>
      ) : (
        <>
          {ranked.length > 0 && <FloatRanking ranked={ranked} caveat={data.float_caveat} today={data.today} />}

          <section className="space-y-4">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-lg font-medium text-ink">
                Where each card is in its cycle
              </h2>
              <span className="shrink-0 text-xs text-faint">soonest to close first</span>
            </div>
            <div className="space-y-3">
              {cards.map((card, i) => (
                <CycleRow key={card.card_id} card={card} today={data.today} index={i} />
              ))}
            </div>
          </section>

          {known.length > 0 && <FactsTable cards={known} />}
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The decision, at the top of the page.
 *
 * Buying the day after a cycle closes gets you the whole next cycle plus its
 * grace period; buying the day before it closes gets you the grace period alone.
 * On a real wallet that is a two-week spread on the same purchase, which is why
 * this is worth ranking rather than leaving the user to work out from the dates
 * below.
 *
 * The caveat is not decoration. This ranks interest-free days and nothing else —
 * it knows nothing about rewards, and the longest float is frequently not the
 * card that earns the most.
 */
function FloatRanking({
  ranked,
  caveat,
  today,
}: {
  ranked: CycleCard[];
  caveat: string;
  today: string;
}) {
  const longest = ranked[0]?.float_days ?? 1;

  return (
    <Panel className="animate-rise overflow-hidden" style={{ animationDelay: "60ms" }}>
      <div className="border-b border-hairline px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-base font-medium text-ink">
            Buying today, interest-free days
          </h2>
          <span className="text-xs tabular-nums text-faint">{formatDate(today)}</span>
        </div>
        <p className="mt-1 text-xs text-faint">
          Days until a purchase made today would actually fall due — the cycle it lands in,
          plus that bill's grace period.
        </p>
      </div>

      <ol className="divide-y divide-hairline">
        {ranked.map((card, i) => (
          <li key={card.card_id} className="flex items-center gap-3 px-5 py-2.5">
            <span className="w-4 shrink-0 text-right text-xs tabular-nums text-faint">
              {i + 1}
            </span>
            <Link
              to={`/cards/${card.card_id}`}
              className="min-w-0 flex-1 truncate text-sm text-ink hover:text-brass"
            >
              {cardName(card)}
            </Link>

            {/* Bar as ranking, not as data — width relative to the longest float. */}
            <span
              aria-hidden
              className="hidden h-1.5 w-32 shrink-0 overflow-hidden rounded-full bg-raised sm:block"
            >
              <span
                className={cn("block h-full rounded-full", i === 0 ? "bg-brass" : "bg-brass/35")}
                style={{ width: `${Math.max(4, ((card.float_days ?? 0) / longest) * 100)}%` }}
              />
            </span>

            <span className="hidden w-40 shrink-0 text-right text-xs tabular-nums text-faint md:block">
              closes {formatDate(card.cycle_end)}
            </span>
            <span
              className={cn(
                "w-20 shrink-0 text-right font-display text-base font-semibold tabular-nums",
                i === 0 ? "text-brass" : "text-ink",
              )}
              title={card.due_basis}
            >
              {card.float_days}d
            </span>
            <ConfidenceMark confidence={card.confidence} card={card} />
          </li>
        ))}
      </ol>

      <p className="flex items-start gap-2 border-t border-hairline bg-raised/40 px-5 py-3 text-xs text-muted">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
        {caveat}
      </p>
    </Panel>
  );
}

function CycleRow({ card, today, index }: { card: CycleCard; today: string; index: number }) {
  return (
    <Panel
      className="animate-rise p-5"
      style={{ animationDelay: `${120 + Math.min(index, 8) * 40}ms` }}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <Link
            to={`/cards/${card.card_id}`}
            className="font-display text-base font-medium text-ink hover:text-brass"
          >
            {cardName(card)}
          </Link>
          <p className="mt-0.5 text-xs text-muted">
            {card.issuer}
            {card.cycle_end && (
              <>
                {" · "}
                {card.days_left_in_cycle === 0
                  ? "closes today"
                  : card.days_left_in_cycle === 1
                    ? "closes tomorrow"
                    : `closes in ${card.days_left_in_cycle} days`}
              </>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {card.float_days !== null && (
            <span className="text-right">
              <span className="font-display text-base font-semibold tabular-nums text-ink">
                {card.float_days}
              </span>
              <span className="ml-1 text-xs text-faint">days float</span>
            </span>
          )}
          <ConfidenceMark confidence={card.confidence} card={card} />
        </div>
      </div>

      {card.unavailable ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-hairline bg-raised/40 p-3 text-xs text-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
          {card.unavailable}
        </p>
      ) : (
        <>
          <div className="mt-4">
            <CycleBar card={card} today={today} />
          </div>

          {/* A bill that exists and can't be seen is a problem; a cycle with
              nothing to bill is not. Only the first gets the amber treatment —
              four false alarms are how the fifth, real one gets ignored. */}
          {card.statements_missing > 0 ? (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-warn/25 bg-warn/[0.06] p-3 text-xs text-warn">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                {card.statements_missing} statement
                {card.statements_missing === 1 ? "" : "s"} missing since{" "}
                {formatDate(card.last_statement_date)} — this card had spending, or a
                balance owing, in {card.statements_missing === 1 ? "that cycle" : "those cycles"}
                , so a bill should have been issued. The cycle above is projected from
                this card's usual dates.
              </span>
            </p>
          ) : (
            card.quiet_cycles > 0 && (
              <p className="mt-3 flex items-start gap-2 rounded-lg border border-hairline bg-raised/40 p-3 text-xs text-muted">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
                <span>
                  No statement since {formatDate(card.last_statement_date)}, and none was
                  due: {card.quiet_cycles} cycle{card.quiet_cycles === 1 ? "" : "s"} passed
                  with no spending and nothing owed, so the issuer had nothing to bill. The
                  cycle above is projected from this card's usual dates.
                </span>
              </p>
            )
          )}

          {card.outstanding !== null && card.outstanding_due_date !== null && (
            <p className="mt-3 text-xs text-muted">
              <span className="text-warn">{formatMoney(card.outstanding)}</span> from the
              last statement is due {formatDate(card.outstanding_due_date)} — a date the
              issuer printed, not a projection.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * The exact figures. A timeline shows shape; this shows numbers you can read off
 * and compare down a column — hence tabular figures and right-aligned days.
 */
function FactsTable({ cards }: { cards: CycleCard[] }) {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-lg font-medium text-ink">The exact dates</h2>
      <Panel className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-hairline text-left">
                <th className="label-micro px-5 py-2 font-normal">Card</th>
                <th className="label-micro px-3 py-2 font-normal">Cycle opened</th>
                <th className="label-micro px-3 py-2 font-normal">Closes</th>
                <th className="label-micro px-3 py-2 font-normal">Statement</th>
                <th className="label-micro px-3 py-2 font-normal">Bill due</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Cycle</th>
                <th className="label-micro px-3 py-2 text-right font-normal">Grace</th>
                <th className="label-micro px-5 py-2 text-right font-normal">Float</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {cards.map((c) => (
                <tr key={c.card_id}>
                  <td className="px-5 py-2.5">
                    <Link
                      to={`/cards/${c.card_id}`}
                      className="text-ink hover:text-brass"
                      title={`${c.close_basis} ${c.due_basis}`}
                    >
                      {cardName(c)}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted">
                    {formatDate(c.cycle_start)}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2.5 tabular-nums text-ink"
                    title={c.close_basis}
                  >
                    {formatDate(c.cycle_end)}
                  </td>
                  {/* Two issuers date the statement after the cycle closes, so
                      this is its own column rather than a repeat of the close. */}
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted">
                    {formatDate(c.statement_expected_on)}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2.5 tabular-nums text-muted"
                    title={c.due_basis}
                  >
                    {formatDate(c.due_expected_on)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-muted">
                    {c.cycle_length_days !== null ? `${c.cycle_length_days}d` : "—"}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right tabular-nums text-muted"
                    title={c.due_basis}
                  >
                    {c.grace_days !== null ? `${c.grace_days}d` : "—"}
                  </td>
                  <td className="px-5 py-2.5 text-right font-medium tabular-nums text-ink">
                    {c.float_days !== null ? `${c.float_days}d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-hairline px-5 py-3 text-xs text-faint">
          Closes, statement, bill due, grace and float are all projected from each card's own
          statement history — hover any of them for the rule and how many statements agree.
          A printed due date on an unpaid bill is shown per card above, where it can't be
          mistaken for one of these.
        </p>
      </Panel>
    </section>
  );
}

/**
 * How much the projection can be trusted, in one glance (§2.7).
 *
 * Only shown when it is *not* the good case — a chip on every row would be noise
 * and would stop being read, which is exactly when the one that matters is
 * missed.
 */
function ConfidenceMark({
  confidence,
  card,
}: {
  confidence: CycleConfidence;
  card: CycleCard;
}) {
  if (confidence === "observed") return null;
  return (
    <Badge
      tone={confidence === "unknown" ? "neutral" : "warn"}
      className="shrink-0 cursor-help border-dashed"
      title={`${card.close_basis} ${card.due_basis}`}
    >
      {confidence === "unknown" ? "No rule yet" : "Estimate"}
    </Badge>
  );
}

function cardName(card: CycleCard): string {
  return card.nickname || card.product_name || `${card.issuer} •••• ${card.last4}`;
}

function LoadingState() {
  return (
    <div className="space-y-8">
      <Panel className="p-5">
        <div className="skeleton h-4 w-56" />
        <div className="mt-4 space-y-2.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-6 w-full" />
          ))}
        </div>
      </Panel>
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Panel key={i} className="p-5">
            <div className="skeleton h-4 w-40" />
            <div className="skeleton mt-4 h-4 w-full" />
            <div className="skeleton mt-3 h-3 w-2/3" />
          </Panel>
        ))}
      </div>
    </div>
  );
}
